// Task #385 — Call Sync Reconciliation tile.
//
// Renders a sibling to the Data Coverage tile that lets an admin/supadmin:
//   1. Generate Report — local heuristic-only scan over a chosen range
//   2. Check Against Dialpad — pulls Dialpad's authoritative call list
//      for every SUSPECT day and confirms the gap
//   3. Correct (fill-only) — enqueues hour-wide forced ingests for the
//      confirmed missing calls
//   4. Check-and-rectify — does (2) then (3) automatically
//
// All reports are persisted (``dialpad_reconciliation_*``) so this same
// page can list previous runs and drill into per-day / per-call results.
//
// Task #389 polish:
//   - DEFINITELY_MISSING severity tier (strongest red) + Stat counter
//   - Severity filter chips on the per-day scan table (UI-only)
//   - Chooser popover on Check-against-Dialpad letting an admin pick
//     which severity buckets to send (defaults: definitely + both
//     suspect tiers; clean OFF)
//   - "Not checked" neutral chip on CLEAN/UNKNOWN PENDING rows so
//     they don't masquerade as work waiting on the worker
//   - Cancelled badge + recovered Cancel button (derives strictly from
//     stage so a stale cancel_requested doesn't permanently disable)
//
// Task #436: admins get report-only access — Generate Report, the run
// list, and the detail drill-in. The re-sync actions (Check / Correct /
// Check-and-rectify / Cancel) remain supadmin-only; admins see a muted
// explanatory line where those buttons would render.
import { Fragment, useMemo, useRef, useState, useEffect } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  api,
  type DialpadReconciliationRangePreset,
  type DialpadReconciliationRunSummary,
  type DialpadReconciliationSeverity,
  type Role,
} from "../../lib/api";
import { fmtDateNZ, fmtDateTime, relativeTime } from "../../lib/format";
import { parseNzDatetimeLocalAsUtcMs } from "../../lib/nz-time";
import { CollapsibleSection } from "./shared";

function todayStartUtcMs(now = new Date()): number {
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

function resolveCustomRangeAsBackend(
  start: string,
  end: string,
): { startMs: number; endMs: number } | null {
  const startMs = parseNzDatetimeLocalAsUtcMs(start);
  const endMs = parseNzDatetimeLocalAsUtcMs(end);
  if (startMs == null || endMs == null) return null;
  const cappedEndMs = Math.min(endMs, todayStartUtcMs());
  if (cappedEndMs <= startMs) return null;
  return { startMs, endMs: cappedEndMs };
}

function sameUtcMinuteMs(apiIso: string, targetMs: number): boolean {
  const apiMs = new Date(apiIso).getTime();
  if (!Number.isFinite(apiMs)) return false;
  return Math.abs(apiMs - targetMs) < 60_000;
}

function fmtRangeLabel(s: string, e: string): string {
  return `${fmtDateTime(s)} NZ — ${fmtDateTime(e)} NZ`;
}

const DEFAULT_CHECK_SEVERITIES: DialpadReconciliationSeverity[] = [
  "DEFINITELY_MISSING",
  "SUSPECT_MISSING",
  "SUSPECT_LOW",
];
const ALL_SEVERITIES: DialpadReconciliationSeverity[] = [
  "DEFINITELY_MISSING",
  "SUSPECT_MISSING",
  "SUSPECT_LOW",
  "CLEAN",
  "UNKNOWN",
];

function severityLabel(s: DialpadReconciliationSeverity): string {
  switch (s) {
    case "DEFINITELY_MISSING":
      return "Definitely missing";
    case "SUSPECT_MISSING":
      return "Suspect missing";
    case "SUSPECT_LOW":
      return "Suspect low";
    case "CLEAN":
      return "Clean";
    case "UNKNOWN":
      return "Unknown";
  }
}

function StageBadge({ stage }: { stage: DialpadReconciliationRunSummary["stage"] }) {
  const styles: Record<string, string> = {
    reported: "bg-muted text-muted-foreground",
    checking: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
    checked: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100",
    correcting:
      "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
    corrected:
      "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100",
    cancelled:
      "bg-slate-200 text-slate-800 dark:bg-slate-700/60 dark:text-slate-100",
    failed: "bg-destructive/15 text-destructive",
  };
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${styles[stage] || "bg-muted"}`}
    >
      {stage}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const styles: Record<string, string> = {
    CLEAN:
      "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100",
    SUSPECT_LOW:
      "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100",
    SUSPECT_MISSING: "bg-destructive/15 text-destructive",
    // Strongest red — the day-rollup considers expected≥floor with
    // zero observed (or <1% of expected) a near-certain gap, distinct
    // from the "we'd expect more than this" suspect tiers.
    DEFINITELY_MISSING:
      "bg-destructive text-destructive-foreground font-semibold",
    UNKNOWN: "bg-muted text-muted-foreground",
  };
  const label = severity.replace(/_/g, " ").toLowerCase();
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${styles[severity] || "bg-muted"}`}
    >
      {label}
    </span>
  );
}

function OutcomeBadge({
  outcome,
  severity,
}: {
  outcome: string;
  severity?: string;
}) {
  // Task #389: a PENDING outcome on a CLEAN/UNKNOWN day isn't really
  // "waiting on the worker" — by default we don't check those buckets,
  // so render a neutral "Not checked" chip instead of the same dim
  // pending pill the suspect days use while truly queued.
  if (
    outcome === "PENDING" &&
    (severity === "CLEAN" || severity === "UNKNOWN")
  ) {
    return (
      <span
        className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground"
        title="Clean and unknown days aren't checked against Dialpad unless you opt in via the chooser."
      >
        Not checked
      </span>
    );
  }
  const styles: Record<string, string> = {
    PENDING: "bg-muted text-muted-foreground",
    CONFIRMED_MISSING: "bg-destructive/15 text-destructive",
    FALSE_ALARM:
      "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100",
    API_ANOMALY:
      "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100",
    SKIPPED: "bg-muted text-muted-foreground",
  };
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${styles[outcome] || "bg-muted"}`}
    >
      {outcome.toLowerCase().replace(/_/g, " ")}
    </span>
  );
}

// Task #389: dropdown that lets the admin pick which severity buckets
// the next Check-against-Dialpad call should scan. Closes on outside
// click / Escape. Body is the *uncontrolled* selection state — parent
// owns the final value only when the admin clicks Run.
function CheckScopeChooser({
  open,
  onClose,
  initial,
  onRun,
  busy,
  label,
}: {
  open: boolean;
  onClose: () => void;
  initial: DialpadReconciliationSeverity[];
  onRun: (sev: DialpadReconciliationSeverity[]) => void;
  busy: boolean;
  label: string;
}) {
  const [sel, setSel] = useState<Set<DialpadReconciliationSeverity>>(
    () => new Set(initial),
  );
  const popRef = useRef<HTMLDivElement | null>(null);

  // Re-seed when re-opened so a previous edit doesn't leak across opens.
  useEffect(() => {
    if (open) setSel(new Set(initial));
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  function toggle(s: DialpadReconciliationSeverity) {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }
  const ordered = ALL_SEVERITIES.filter((s) => sel.has(s));
  return (
    <div
      ref={popRef}
      className="absolute z-40 mt-1 w-72 rounded-md border border-border bg-popover text-popover-foreground shadow-lg p-3 space-y-2"
      role="dialog"
      aria-label="Choose check scope"
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="space-y-1">
        {ALL_SEVERITIES.map((s) => (
          <label
            key={s}
            className="flex items-center gap-2 text-sm cursor-pointer select-none"
          >
            <input
              type="checkbox"
              checked={sel.has(s)}
              onChange={() => toggle(s)}
              className="rounded border-input"
            />
            <span>{severityLabel(s)}</span>
          </label>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Defaults to the three missing tiers. Tick Clean to force a sweep
        of days that look fine locally.
      </p>
      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={() => setSel(new Set(DEFAULT_CHECK_SEVERITIES))}
          className="text-xs px-2 py-1 rounded border border-input hover:bg-muted"
        >
          Reset
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-2 py-1 rounded border border-input hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || ordered.length === 0}
            onClick={() => onRun(ordered)}
            className="text-xs px-3 py-1 rounded bg-primary text-primary-foreground disabled:opacity-50"
          >
            {busy ? "Starting…" : "Run check"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RunDetailModal({
  runId,
  isSuperAdmin,
  onClose,
}: {
  runId: string;
  isSuperAdmin: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["dialpad-reconciliation-run", runId],
    queryFn: () => api.dialpadReconciliationRun(runId),
    refetchInterval: (qq) => {
      const stage = qq.state.data?.run.stage;
      return stage === "checking" || stage === "correcting" ? 3000 : false;
    },
    placeholderData: keepPreviousData,
  });
  const checkM = useMutation({
    mutationFn: (sev: DialpadReconciliationSeverity[]) =>
      api.dialpadReconciliationCheck(runId, sev),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-run", runId] });
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-runs"] });
    },
  });
  const correctM = useMutation({
    mutationFn: () => api.dialpadReconciliationCorrect(runId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-run", runId] });
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-runs"] });
    },
  });
  const cancelM = useMutation({
    mutationFn: () => api.dialpadReconciliationCancel(runId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-run", runId] });
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-runs"] });
    },
  });
  const [chooserOpen, setChooserOpen] = useState(false);
  // Task #389: severity chips on the per-day table (component-local;
  // doesn't change what was scanned, just what's shown).
  const [shownSeverities, setShownSeverities] = useState<
    Set<DialpadReconciliationSeverity>
  >(() => new Set(ALL_SEVERITIES));

  const data = q.data;
  const run = data?.run;
  const checkErr = checkM.error as { message?: string } | null;
  const correctErr = correctM.error as { message?: string } | null;

  // Task #389: Cancel button derives strictly from current stage so a
  // stale cancel_requested flag (worker died, etc.) doesn't make the
  // button permanently dim. cancel_requested only changes the label.
  const inCheck = run?.stage === "checking";
  const cancelDisabled = !inCheck || cancelM.isPending || !!run?.cancel_requested;

  const filteredDays = useMemo(() => {
    if (!data) return [];
    return data.days.filter((d) =>
      shownSeverities.has(d.severity as DialpadReconciliationSeverity),
    );
  }, [data, shownSeverities]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-8 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Reconciliation run detail"
      onClick={onClose}
    >
      <div
        className="bg-card text-card-foreground rounded-lg shadow-2xl border border-border w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <div className="min-w-0">
            <h2 className="font-semibold text-foreground">
              Reconciliation run
            </h2>
            {run && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {fmtRangeLabel(run.range_start, run.range_end)} ·{" "}
                {fmtDateTime(run.created_at)} · by {run.triggered_by || "—"}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {run && <StageBadge stage={run.stage} />}
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-1 rounded border border-input hover:bg-muted"
            >
              Close
            </button>
          </div>
        </header>
        <div className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
          {q.isLoading && (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
          {run && data && (
            <Fragment>
              <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Scanned days" value={run.scanned_days} />
                <Stat
                  label="Definitely missing"
                  value={run.definitely_missing_days}
                  accent={run.definitely_missing_days > 0}
                />
                <Stat label="Suspect" value={run.suspect_days} accent />
                <Stat label="Clean" value={run.clean_days} />
                <Stat label="Unknown" value={run.unknown_days} />
                <Stat
                  label="Confirmed missing"
                  value={run.confirmed_missing_calls}
                  accent
                />
                <Stat label="Re-ingested" value={run.reingested_calls} />
                <Stat
                  label="Failed re-ingest"
                  value={run.failed_reingest_calls}
                  accent={run.failed_reingest_calls > 0}
                />
                <Stat
                  label="Updated"
                  value={relativeTime(run.updated_at)}
                  small
                />
              </section>

              {(run.stage === "checking" ||
                (run.progress_suspect_total > 0 &&
                  run.progress_suspect_done < run.progress_suspect_total &&
                  run.stage !== "cancelled")) && (
                <section
                  className="rounded border border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-900/20 px-3 py-2"
                  aria-live="polite"
                >
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <div className="text-amber-900 dark:text-amber-100 min-w-0">
                      <span className="font-medium">Checking against Dialpad…</span>
                      {run.progress_day && (
                        <>
                          {" "}
                          scanning{" "}
                          <span className="font-mono">
                            {fmtDateNZ(new Date(run.progress_day))}
                          </span>{" "}
                          ({run.progress_day_calls.toLocaleString()} calls pulled)
                        </>
                      )}
                    </div>
                    <div className="text-xs text-amber-900/80 dark:text-amber-100/80 whitespace-nowrap">
                      {run.progress_suspect_done} / {run.progress_suspect_total}{" "}
                      suspect days
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 rounded bg-amber-200 dark:bg-amber-900/40 overflow-hidden">
                    <div
                      className="h-full bg-amber-500 transition-all"
                      style={{
                        width: `${
                          run.progress_suspect_total > 0
                            ? Math.min(
                                100,
                                Math.round(
                                  (run.progress_suspect_done /
                                    run.progress_suspect_total) *
                                    100,
                                ),
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </section>
              )}

              <section className="flex flex-wrap gap-2 items-center relative">
                {isSuperAdmin && (
                  <Fragment>
                    <div className="relative">
                      <button
                        type="button"
                        disabled={
                          checkM.isPending ||
                          run.stage === "checking" ||
                          run.stage === "correcting"
                        }
                        onClick={() => setChooserOpen((v) => !v)}
                        className="text-sm px-3 py-1.5 rounded bg-primary text-primary-foreground disabled:opacity-50"
                      >
                        {run.stage === "checking"
                          ? "Checking…"
                          : "Check against Dialpad…"}
                      </button>
                      <CheckScopeChooser
                        open={chooserOpen}
                        onClose={() => setChooserOpen(false)}
                        initial={DEFAULT_CHECK_SEVERITIES}
                        busy={checkM.isPending}
                        label="Scope of next check"
                        onRun={(sev) => {
                          setChooserOpen(false);
                          checkM.mutate(sev);
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      disabled={
                        correctM.isPending ||
                        run.confirmed_missing_calls === 0 ||
                        run.stage === "checking" ||
                        run.stage === "correcting"
                      }
                      onClick={() => correctM.mutate()}
                      className="text-sm px-3 py-1.5 rounded border border-input hover:bg-muted disabled:opacity-50"
                    >
                      {run.stage === "correcting"
                        ? "Correcting…"
                        : `Correct (${run.confirmed_missing_calls})`}
                    </button>
                    <button
                      type="button"
                      disabled={cancelDisabled}
                      onClick={() => cancelM.mutate()}
                      className="text-sm px-3 py-1.5 rounded border border-destructive text-destructive hover:bg-destructive/10 disabled:opacity-50"
                      title="Stop the in-flight check early; partial results are kept."
                    >
                      {inCheck && run.cancel_requested
                        ? "Cancelling…"
                        : "Cancel"}
                    </button>
                    {checkErr && (
                      <span className="text-xs text-destructive">
                        {checkErr.message}
                      </span>
                    )}
                    {correctErr && (
                      <span className="text-xs text-destructive">
                        {correctErr.message}
                      </span>
                    )}
                  </Fragment>
                )}
                {!isSuperAdmin && (
                  <span className="text-xs text-muted-foreground">
                    Re-sync actions (check / correct / cancel) require a super
                    admin.
                  </span>
                )}
                {run.last_error && (
                  <span className="text-xs text-destructive">
                    {run.last_error}
                  </span>
                )}
              </section>

              <section>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <h3 className="font-semibold text-sm">Per-day scan</h3>
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-xs text-muted-foreground mr-1">
                      Show:
                    </span>
                    {ALL_SEVERITIES.map((s) => {
                      const active = shownSeverities.has(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          aria-pressed={active}
                          onClick={() =>
                            setShownSeverities((prev) => {
                              const next = new Set(prev);
                              if (next.has(s)) next.delete(s);
                              else next.add(s);
                              return next;
                            })
                          }
                          className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                            active
                              ? "border-foreground/40 bg-foreground/10"
                              : "border-input opacity-50 hover:opacity-80"
                          }`}
                        >
                          {severityLabel(s)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="overflow-x-auto border border-border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">Day</th>
                        <th className="text-left px-3 py-2">Severity</th>
                        <th className="text-right px-3 py-2">Observed</th>
                        <th className="text-right px-3 py-2">Expected</th>
                        <th className="text-left px-3 py-2">Check</th>
                        <th className="text-right px-3 py-2">Dialpad</th>
                        <th className="text-right px-3 py-2">Missing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDays.map((d) => (
                        <tr
                          key={d.day}
                          className="border-t border-border align-top"
                        >
                          <td className="px-3 py-2 whitespace-nowrap">
                            {fmtDateNZ(new Date(d.day))}
                          </td>
                          <td className="px-3 py-2">
                            <SeverityBadge severity={d.severity} />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {d.observed_count}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {d.expected_median != null
                              ? d.expected_median.toFixed(1)
                              : "—"}
                          </td>
                          <td className="px-3 py-2">
                            <OutcomeBadge
                              outcome={d.check_outcome}
                              severity={d.severity}
                            />
                            {d.check_error && (
                              <div className="text-xs text-destructive mt-1">
                                {d.check_error}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {d.dialpad_count ?? "—"}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {d.missing_call_count || "—"}
                          </td>
                        </tr>
                      ))}
                      {filteredDays.length === 0 && (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-3 py-4 text-center text-sm text-muted-foreground"
                          >
                            {data.days.length === 0
                              ? "No days scanned."
                              : "No days match the selected severities."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {data.missing_calls.length > 0 && (
                <section>
                  <h3 className="font-semibold mb-2 text-sm">
                    Missing calls ({data.missing_calls_total}
                    {data.missing_calls_truncated ? " — first 200 shown" : ""})
                  </h3>
                  <div className="overflow-x-auto border border-border rounded max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2">Started</th>
                          <th className="text-left px-3 py-2">Call ID</th>
                          <th className="text-left px-3 py-2">Agent</th>
                          <th className="text-left px-3 py-2">Direction</th>
                          <th className="text-left px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.missing_calls.map((m) => (
                          <tr key={m.call_id} className="border-t border-border">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {m.started_at
                                ? fmtDateTime(m.started_at)
                                : "—"}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {m.call_id}
                            </td>
                            <td className="px-3 py-2">
                              {m.agent_name || m.agent_id || "—"}
                            </td>
                            <td className="px-3 py-2">{m.direction || "—"}</td>
                            <td className="px-3 py-2">
                              <span className="text-xs">{m.status}</span>
                              {m.last_error && (
                                <div className="text-xs text-destructive">
                                  {m.last_error}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent = false,
  small = false,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
  small?: boolean;
}) {
  return (
    <div className="border border-border rounded p-2 bg-card">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={`mt-0.5 ${
          small ? "text-sm" : "text-xl"
        } font-semibold tabular-nums ${
          accent && value !== 0 && value !== "0" ? "text-destructive" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export function DialpadReconciliationTile({ role }: { role: Role }) {
  // Task #436: admins get report-only access (generate / list / drill in);
  // the re-sync actions (check / correct / cancel) stay supadmin-only.
  const allowed = role === "supadmin" || role === "admin";
  const isSuperAdmin = role === "supadmin";
  const qc = useQueryClient();
  const [preset, setPreset] =
    useState<DialpadReconciliationRangePreset>("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [tileChooserOpen, setTileChooserOpen] = useState(false);
  const resolvedCustomRange = useMemo(
    () =>
      preset === "custom"
        ? resolveCustomRangeAsBackend(customStart, customEnd)
        : null,
    [preset, customStart, customEnd],
  );

  const runsQ = useQuery({
    queryKey: ["dialpad-reconciliation-runs"],
    queryFn: () => api.dialpadReconciliationRuns(20, 0),
    enabled: allowed,
    refetchInterval: (qq) => {
      const runs = qq.state.data?.runs || [];
      const inflight = runs.some(
        (r) => r.stage === "checking" || r.stage === "correcting",
      );
      return inflight ? 3000 : 15000;
    },
    placeholderData: keepPreviousData,
  });

  function buildBody() {
    return {
      range_preset: preset,
      start:
        preset === "custom" && resolvedCustomRange
          ? new Date(resolvedCustomRange.startMs).toISOString()
          : null,
      end:
        preset === "custom" && resolvedCustomRange
          ? new Date(resolvedCustomRange.endMs).toISOString()
          : null,
    };
  }

  const generateM = useMutation({
    mutationFn: () =>
      api.createDialpadReconciliationRun(buildBody()),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-runs"] });
      setOpenRunId(run.id);
    },
  });
  const checkRectifyM = useMutation({
    mutationFn: () =>
      api.dialpadReconciliationCheckAndRectify(buildBody()),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-runs"] });
      setOpenRunId(res.run_id);
    },
  });

  // Resolve "the most recent run for the currently-selected scope" so the
  // tile's Check / Correct / Cancel buttons act on it directly without
  // forcing the admin to open the detail modal. Scope match falls back to
  // "same preset" — the preset is enough fidelity for the common cases
  // (30d / 60d / all); custom also matches the selected NZ-time start/end.
  const latestForScope = (runsQ.data?.runs || []).find((r) => {
    if (r.range_preset !== preset) return false;
    if (preset !== "custom") return true;
    if (!resolvedCustomRange) return false;
    return (
      sameUtcMinuteMs(r.range_start, resolvedCustomRange.startMs) &&
      sameUtcMinuteMs(r.range_end, resolvedCustomRange.endMs)
    );
  });

  const cardCheckM = useMutation({
    mutationFn: ({
      rid,
      sev,
    }: {
      rid: string;
      sev: DialpadReconciliationSeverity[];
    }) => api.dialpadReconciliationCheck(rid, sev),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-runs"] });
    },
  });
  const cardCorrectM = useMutation({
    mutationFn: (rid: string) =>
      api.dialpadReconciliationCorrect(rid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-runs"] });
    },
  });
  const cardCancelM = useMutation({
    mutationFn: (rid: string) =>
      api.dialpadReconciliationCancel(rid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-reconciliation-runs"] });
    },
  });

  const genErr = generateM.error as { message?: string } | null;
  const cnrErr = checkRectifyM.error as { message?: string } | null;
  const checkErr = cardCheckM.error as { message?: string } | null;
  const correctErr = cardCorrectM.error as { message?: string } | null;
  const cancelErr = cardCancelM.error as { message?: string } | null;
  const customMissing = preset === "custom" && !resolvedCustomRange;
  const busy = generateM.isPending || checkRectifyM.isPending;

  const checkDisabled =
    !latestForScope ||
    cardCheckM.isPending ||
    latestForScope.stage === "checking" ||
    latestForScope.stage === "correcting";
  const correctDisabled =
    !latestForScope ||
    cardCorrectM.isPending ||
    latestForScope.confirmed_missing_calls === 0 ||
    latestForScope.stage === "checking" ||
    latestForScope.stage === "correcting";
  // Task #389: derive strictly from stage so a stuck cancel_requested
  // flag doesn't make the button permanently dim across runs.
  const inCheck = latestForScope?.stage === "checking";
  const cancelDisabled =
    !latestForScope ||
    cardCancelM.isPending ||
    !inCheck ||
    !!latestForScope.cancel_requested;

  if (!allowed) {
    return null;
  }

  return (
    <Fragment>
      <CollapsibleSection
        title="Call Sync Reconciliation"
        subtitle="Detect and fill Dialpad calls we may have missed during a previously-completed sync."
        defaultExpanded={false}
        ariaLabel="Call Sync Reconciliation"
      >
        <div className="p-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground">
                Range
              </label>
              <select
                value={preset}
                onChange={(e) =>
                  setPreset(
                    e.target.value as DialpadReconciliationRangePreset,
                  )
                }
                className="border border-input rounded px-2 py-1 text-sm bg-background"
              >
                <option value="30d">Last 30 days</option>
                <option value="60d">Last 60 days</option>
                <option value="all">All time</option>
                <option value="custom">Custom…</option>
              </select>
            </div>
            {preset === "custom" && (
              <Fragment>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-muted-foreground">
                    Start (NZ time)
                  </label>
                  <input
                    type="datetime-local"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="border border-input rounded px-2 py-1 text-sm bg-background"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-muted-foreground">
                    End (NZ time)
                  </label>
                  <input
                    type="datetime-local"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="border border-input rounded px-2 py-1 text-sm bg-background"
                  />
                </div>
              </Fragment>
            )}
            <button
              type="button"
              disabled={busy || customMissing}
              onClick={() => generateM.mutate()}
              className="text-sm px-3 py-1.5 rounded bg-primary text-primary-foreground disabled:opacity-50"
            >
              {generateM.isPending ? "Generating…" : "Generate Report"}
            </button>
            {isSuperAdmin && (
              <Fragment>
                <div className="relative">
                  <button
                    type="button"
                    disabled={checkDisabled}
                    onClick={() => setTileChooserOpen((v) => !v)}
                    className="text-sm px-3 py-1.5 rounded border border-input hover:bg-muted disabled:opacity-50"
                    title={
                      latestForScope
                        ? "Pull Dialpad's authoritative call list for the chosen severities in the latest report."
                        : "Generate a report first."
                    }
                  >
                    {latestForScope?.stage === "checking"
                      ? "Checking…"
                      : "Check against Dialpad…"}
                  </button>
                  <CheckScopeChooser
                    open={tileChooserOpen}
                    onClose={() => setTileChooserOpen(false)}
                    initial={DEFAULT_CHECK_SEVERITIES}
                    busy={cardCheckM.isPending}
                    label="Scope of next check"
                    onRun={(sev) => {
                      setTileChooserOpen(false);
                      if (latestForScope) {
                        cardCheckM.mutate({ rid: latestForScope.id, sev });
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  disabled={correctDisabled}
                  onClick={() =>
                    latestForScope && cardCorrectM.mutate(latestForScope.id)
                  }
                  className="text-sm px-3 py-1.5 rounded border border-input hover:bg-muted disabled:opacity-50"
                  title="Fill the confirmed missing calls by re-ingesting their hour windows."
                >
                  {latestForScope?.stage === "correcting"
                    ? "Correcting…"
                    : `Correct${
                        latestForScope?.confirmed_missing_calls
                          ? ` (${latestForScope.confirmed_missing_calls})`
                          : ""
                      }`}
                </button>
                <button
                  type="button"
                  disabled={busy || customMissing}
                  onClick={() => checkRectifyM.mutate()}
                  className="text-sm px-3 py-1.5 rounded border border-input hover:bg-muted disabled:opacity-50"
                  title="Generate, check against Dialpad, then auto-fill missing calls."
                >
                  {checkRectifyM.isPending ? "Running…" : "Check and rectify"}
                </button>
                <button
                  type="button"
                  disabled={cancelDisabled}
                  onClick={() =>
                    latestForScope && cardCancelM.mutate(latestForScope.id)
                  }
                  className="text-sm px-3 py-1.5 rounded border border-destructive text-destructive hover:bg-destructive/10 disabled:opacity-50"
                  title="Stop the in-flight check; partial per-day results are kept."
                >
                  {inCheck && latestForScope?.cancel_requested
                    ? "Cancelling…"
                    : "Cancel check"}
                </button>
              </Fragment>
            )}
            {!isSuperAdmin && (
              <span className="text-xs text-muted-foreground">
                Re-sync actions (check / correct / cancel) require a super
                admin.
              </span>
            )}
            {genErr && (
              <span className="text-xs text-destructive">{genErr.message}</span>
            )}
            {isSuperAdmin && cnrErr && (
              <span className="text-xs text-destructive">{cnrErr.message}</span>
            )}
            {isSuperAdmin && checkErr && (
              <span className="text-xs text-destructive">
                {checkErr.message}
              </span>
            )}
            {isSuperAdmin && correctErr && (
              <span className="text-xs text-destructive">
                {correctErr.message}
              </span>
            )}
            {isSuperAdmin && cancelErr && (
              <span className="text-xs text-destructive">
                {cancelErr.message}
              </span>
            )}
          </div>
          {isSuperAdmin && latestForScope && (
            <p className="text-xs text-muted-foreground">
              Acting on most recent {latestForScope.range_preset} run from{" "}
              {relativeTime(latestForScope.created_at)} — stage{" "}
              <span className="font-medium">{latestForScope.stage}</span>.
              Generate Report to start a fresh one.
            </p>
          )}

          <div>
            <h3 className="font-semibold text-sm mb-2">Recent runs</h3>
            <div className="overflow-x-auto border border-border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Range</th>
                    <th className="text-left px-3 py-2">By</th>
                    <th className="text-left px-3 py-2">Stage</th>
                    <th className="text-right px-3 py-2">Def. missing</th>
                    <th className="text-right px-3 py-2">Suspect</th>
                    <th className="text-right px-3 py-2">Confirmed</th>
                    <th className="text-right px-3 py-2">Re-ingested</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(runsQ.data?.runs || []).map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {relativeTime(r.created_at)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {fmtRangeLabel(r.range_start, r.range_end)}{" "}
                        <span className="text-xs text-muted-foreground">
                          ({r.range_preset})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.triggered_by || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <StageBadge stage={r.stage} />
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums ${
                          r.definitely_missing_days > 0
                            ? "text-destructive font-semibold"
                            : ""
                        }`}
                      >
                        {r.definitely_missing_days}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {r.suspect_days}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {r.confirmed_missing_calls}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {r.reingested_calls}
                        {r.failed_reingest_calls > 0 && (
                          <span className="text-destructive">
                            {" "}
                            (-{r.failed_reingest_calls})
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setOpenRunId(r.id)}
                          className="text-xs px-2 py-0.5 rounded border border-input hover:bg-muted"
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(runsQ.data?.runs || []).length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-3 py-4 text-center text-sm text-muted-foreground"
                      >
                        No reconciliation runs yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CollapsibleSection>
      {openRunId && (
        <RunDetailModal
          runId={openRunId}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setOpenRunId(null)}
        />
      )}
    </Fragment>
  );
}
